/**
 * 章节目录树组件
 *
 * 显示工作区内的所有章节，按序号排序，支持点击选择章节
 * 点击章节项可触发选中回调，支持刷新章节列表
 * 支持卷管理和章节名称直接编辑
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { Chapter } from '../../types/chapter';
import type { Volume, VolumeCreate, VolumeUpdate } from '../../types/volume';
import { ChapterService } from '../../services/chapter';
import { volumeApi, OverlappingRangeError, ValidationError, DuplicateNameError } from '../../services/volumeApi';
import { CreateChapterDialog } from './CreateChapterDialog';
import { useAuthStore } from '../../store/authStore';
import { useRefreshStore } from '../../store/refreshStore';
import { useBranchStore } from '../../store/branchStore';

// ==================== SVG图标常量 ====================

const ICONS = {
  refresh: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  search: (
    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  add: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
    </svg>
  ),
  delete: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  lock: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  ),
  unlock: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
    </svg>
  ),
  volume: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  chevronDown: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  ),
  chevronRight: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  ),
  empty: (
    <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  error: (
    <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  loadingLarge: <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600" />,
  loadingSmall: <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600" />,
  edit: (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  ),
} as const;

// ==================== 可编辑标题组件 ====================

interface EditableTitleProps {
  title: string;
  onSave: (newTitle: string) => Promise<void> | void;
  className?: string;
  inputClassName?: string;
  maxLength?: number;
}

const EditableTitle: React.FC<EditableTitleProps> = ({
  title,
  onSave,
  className = '',
  inputClassName = '',
  maxLength = 100
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    setIsEditing(true);
    setEditValue(title);
  };

  const handleSave = async () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === title) {
      setIsEditing(false);
      setEditValue(title);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmed);
      setIsEditing(false);
    } catch (error) {
      console.error('保存失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(title);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1 flex-1">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value.slice(0, maxLength))}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          disabled={isSaving}
          className={`flex-1 px-2 py-0.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400 ${inputClassName}`}
        />
        {isSaving && ICONS.loadingSmall}
      </div>
    );
  }

  return (
    <span
      onClick={handleClick}
      className={`cursor-pointer hover:text-gray-600 hover:bg-gray-100 rounded px-1 -mx-1 transition-colors group ${className}`}
      title="点击修改名称"
    >
      {title}
      <span className="inline-block ml-1 opacity-0 group-hover:opacity-100 text-gray-400">
        {ICONS.edit}
      </span>
    </span>
  );
};

// ==================== 子组件 ====================

interface VolumeGroupProps {
  volume: Volume;
  chapters: Chapter[];
  selectedChapterId?: number;
  expanded: boolean;
  onToggle: () => void;
  onSelect: (chapter: Chapter) => void;
  onDelete: (chapter: Chapter) => void;
  onLockToggle?: (chapter: Chapter) => void;
  lockingChapterId?: number | null;
  onVolumeUpdate?: (volumeId: number, data: VolumeUpdate) => Promise<void>;
}

const VolumeGroup: React.FC<VolumeGroupProps> = ({
  volume,
  chapters,
  selectedChapterId,
  expanded,
  onToggle,
  onSelect,
  onDelete,
  onLockToggle,
  lockingChapterId,
  onVolumeUpdate
}) => {
  const volumeChapters = chapters.filter(
    ch => ch.num >= volume.start_chapter && ch.num <= volume.end_chapter
  );

  if (volumeChapters.length === 0) return null;

  return (
    <div className="mb-2">
      <div
        onClick={onToggle}
        className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
      >
        <span className="text-gray-500">
          {expanded ? ICONS.chevronDown : ICONS.chevronRight}
        </span>
        <span className="text-gray-600">
          {ICONS.volume}
        </span>
        {onVolumeUpdate ? (
          <EditableTitle
            title={volume.name}
            onSave={(newName) => onVolumeUpdate(volume.id, { name: newName, start_chapter: volume.start_chapter, end_chapter: volume.end_chapter })}
            className="font-medium text-gray-800"
            inputClassName="font-medium"
          />
        ) : (
          <span className="font-medium text-gray-800">{volume.name}</span>
        )}
        <span className="text-xs text-gray-400 ml-2">
          (第{volume.start_chapter}-{volume.end_chapter}章, {volumeChapters.length}章)
        </span>
      </div>
      {expanded && (
        <div className="mt-1 space-y-1 border-l-2 border-gray-200 ml-2 pl-2">
          {volumeChapters.map(chapter => (
            <ChapterItem
              key={chapter.id}
              chapter={chapter}
              isSelected={selectedChapterId === chapter.id}
              onSelect={onSelect}
              onDelete={onDelete}
              onLockToggle={onLockToggle}
              isLocking={lockingChapterId === chapter.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface UnassignedGroupProps {
  chapters: Chapter[];
  volumes: Volume[];
  selectedChapterId?: number;
  expanded: boolean;
  onToggle: () => void;
  onSelect: (chapter: Chapter) => void;
  onDelete: (chapter: Chapter) => void;
  onLockToggle?: (chapter: Chapter) => void;
  lockingChapterId?: number | null;
}

const UnassignedGroup: React.FC<UnassignedGroupProps> = ({
  chapters,
  volumes,
  selectedChapterId,
  expanded,
  onToggle,
  onSelect,
  onDelete,
  onLockToggle,
  lockingChapterId
}) => {
  const unassignedChapters = chapters.filter(ch => {
    return !volumes.some(vol => ch.num >= vol.start_chapter && ch.num <= vol.end_chapter);
  });

  if (unassignedChapters.length === 0) return null;

  return (
    <div className="mb-2">
      <div
        onClick={onToggle}
        className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
      >
        <span className="text-gray-500">
          {expanded ? ICONS.chevronDown : ICONS.chevronRight}
        </span>
        <span className="font-medium text-gray-600">未分卷章节</span>
        <span className="text-xs text-gray-400 ml-2">
          ({unassignedChapters.length}章)
        </span>
      </div>
      {expanded && (
        <div className="mt-1 space-y-1 border-l-2 border-gray-200 ml-2 pl-2">
          {unassignedChapters.map(chapter => (
            <ChapterItem
              key={chapter.id}
              chapter={chapter}
              isSelected={selectedChapterId === chapter.id}
              onSelect={onSelect}
              onDelete={onDelete}
              onLockToggle={onLockToggle}
              isLocking={lockingChapterId === chapter.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface ChapterItemProps {
  chapter: Chapter;
  isSelected: boolean;
  onSelect: (chapter: Chapter) => void;
  onDelete: (chapter: Chapter) => void;
  onLockToggle?: (chapter: Chapter) => void;
  isLocking?: boolean;
  onChapterUpdate?: (chapterId: number, newName: string) => Promise<void>;
}

// 章节列表项子组件
const ChapterItem: React.FC<ChapterItemProps> = ({
  chapter,
  isSelected,
  onSelect,
  onDelete,
  onLockToggle,
  isLocking,
  onChapterUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(chapter.name);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 进入编辑模式时聚焦并选中
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = useCallback(() => {
    onSelect(chapter);
  }, [chapter, onSelect]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // 双击进入编辑模式
    if (onChapterUpdate) {
      setIsEditing(true);
      setEditValue(chapter.name);
    }
  }, [chapter.name, onChapterUpdate]);

  const handleSave = async () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === chapter.name) {
      setIsEditing(false);
      setEditValue(chapter.name);
      return;
    }

    setIsSaving(true);
    try {
      await onChapterUpdate?.(chapter.id, trimmed);
      setIsEditing(false);
    } catch (error) {
      console.error('保存章节名称失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(chapter.name);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    // 公共章节不允许删除
    if (chapter.is_common) {
      return;
    }
    onDelete(chapter);
  }, [chapter, onDelete]);

  const handleLockClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onLockToggle && !isLocking && !chapter.is_common) {
      onLockToggle(chapter);
    }
  }, [chapter, onLockToggle, isLocking]);

  // 判断章节是否锁定
  const isLocked = chapter.is_common || chapter.is_locked;

  return (
    <div
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      className={`
        p-3 rounded-xl cursor-pointer transition-all duration-150
        ${isSelected ? 'bg-gray-100 border-l-4 border-gray-800' : 'hover:bg-gray-50 border-l-4 border-transparent'}
        ${isLocked ? 'opacity-80' : ''}
      `}
      title={onChapterUpdate ? '双击重命名章节' : undefined}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
              第{chapter.num}章
            </span>
            {isEditing ? (
              <div className="flex items-center gap-1 flex-1">
                <input
                  ref={inputRef}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value.slice(0, 200))}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSave}
                  disabled={isSaving}
                  className="flex-1 px-2 py-0.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400 font-medium"
                  onClick={(e) => e.stopPropagation()}
                />
                {isSaving && ICONS.loadingSmall}
              </div>
            ) : (
              <h3 
                className={`font-medium truncate ${isSelected ? 'text-gray-900' : 'text-gray-800'}`}
              >
                {chapter.name}
              </h3>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {chapter.word_count.toLocaleString()} 字
            {chapter.is_common && <span className="ml-2 text-amber-600">（公共章节）</span>}
          </div>
        </div>
        <div className="flex gap-1">
          {/* 锁定按钮 - 公共章节不允许切换锁定状态 */}
          {onLockToggle && !chapter.is_common && (
            <button
              onClick={handleLockClick}
              disabled={isLocking}
              className={`p-1 transition-colors rounded-lg ${
                chapter.is_locked
                  ? 'text-amber-600 hover:bg-amber-50'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              } ${isLocking ? 'opacity-50 cursor-wait' : ''}`}
              title={chapter.is_locked ? '解锁章节（AI将无法修改）' : '锁定章节（AI将无法修改）'}
            >
              {isLocking ? ICONS.loadingSmall : chapter.is_locked ? ICONS.lock : ICONS.unlock}
            </button>
          )}
          {/* 非公共章节才显示删除按钮 */}
          {!chapter.is_common && (
            <button
              onClick={handleDelete}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
              title="删除章节"
            >
              {ICONS.delete}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== 新增菜单组件 ====================

interface AddMenuProps {
  onAddChapter: () => void;
  onAddVolume: () => void;
}

const AddMenu: React.FC<AddMenuProps> = ({ onAddChapter, onAddVolume }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2"
      >
        {ICONS.add}
        新增
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
          <button
            onClick={() => {
              onAddChapter();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            新增章节
          </button>
          <button
            onClick={() => {
              onAddVolume();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            {ICONS.volume}
            新增卷
          </button>
        </div>
      )}
    </div>
  );
};

// ==================== 卷表单对话框 ====================

interface VolumeFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: number;
  volumes: Volume[];
  onSuccess: () => void;
  editingVolume?: Volume | null;
}

const VolumeFormDialog: React.FC<VolumeFormDialogProps> = ({
  isOpen,
  onClose,
  workspaceId,
  volumes,
  onSuccess,
  editingVolume
}) => {
  const [formData, setFormData] = useState<VolumeCreate>({
    name: '',
    start_chapter: 1,
    end_chapter: 1
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (editingVolume) {
        setFormData({
          name: editingVolume.name,
          start_chapter: editingVolume.start_chapter,
          end_chapter: editingVolume.end_chapter
        });
      } else {
        // 自动计算下一卷的起始章节
        const maxEndChapter = volumes.length > 0 
          ? Math.max(...volumes.map(v => v.end_chapter))
          : 0;
        const nextStart = maxEndChapter + 1;
        setFormData({
          name: '',
          start_chapter: nextStart,
          end_chapter: nextStart
        });
      }
      setErrors({});
      setFormError(null);
    }
  }, [isOpen, editingVolume, volumes]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = '卷名不能为空';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = '卷名长度不能超过100字符';
    }
    
    if (formData.start_chapter < 1) {
      newErrors.start_chapter = '起始章节不能小于1';
    }
    
    if (formData.end_chapter < 1) {
      newErrors.end_chapter = '终止章节不能小于1';
    }
    
    if (formData.start_chapter > formData.end_chapter) {
      newErrors.end_chapter = '终止章节必须大于等于起始章节';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormError(null);

    try {
      if (editingVolume) {
        const updateData: VolumeUpdate = {
          name: formData.name,
          start_chapter: formData.start_chapter,
          end_chapter: formData.end_chapter
        };
        await volumeApi.updateVolume(editingVolume.id, updateData);
      } else {
        await volumeApi.createVolume(workspaceId, formData);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('保存卷失败:', error);
      if (error instanceof OverlappingRangeError) {
        setErrors({ end_chapter: error.message });
      } else if (error instanceof DuplicateNameError) {
        setErrors({ name: error.message });
      } else if (error instanceof ValidationError) {
        setFormError(error.message);
      } else {
        setFormError('操作失败，请稍后重试');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-sm w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {editingVolume ? '编辑卷' : '新增卷'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {formError}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              卷名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="例如：第一卷：初入江湖"
              maxLength={100}
              disabled={isSubmitting}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                起始章节 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.start_chapter}
                onChange={(e) => setFormData(prev => ({ ...prev, start_chapter: parseInt(e.target.value) || 1 }))}
                min={1}
                disabled={isSubmitting}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                  errors.start_chapter ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.start_chapter && <p className="mt-1 text-sm text-red-600">{errors.start_chapter}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                终止章节 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.end_chapter}
                onChange={(e) => setFormData(prev => ({ ...prev, end_chapter: parseInt(e.target.value) || 1 }))}
                min={1}
                disabled={isSubmitting}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                  errors.end_chapter ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.end_chapter && <p className="mt-1 text-sm text-red-600">{errors.end_chapter}</p>}
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-md transition-colors disabled:opacity-50"
            >
              {isSubmitting ? '保存中...' : (editingVolume ? '更新' : '创建')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// 组件Props接口
interface ChapterTreeProps {
  /** 工作区ID */
  workspaceId: number;
  /** 选中的章节ID */
  selectedChapterId?: number;
  /** 章节选中回调，isUserClick 表示是否为用户手动点击 */
  onChapterSelect?: (chapter: Chapter, isUserClick?: boolean) => void;
  /** 新增章节回调 */
  onChapterCreate?: () => void;
  /** ChapterService实例（可选，用于外部注入） */
  chapterService?: ChapterService;
}

export const ChapterTree: React.FC<ChapterTreeProps> = ({
  workspaceId,
  selectedChapterId,
  onChapterSelect,
  onChapterCreate,
  chapterService: externalChapterService
}) => {
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [filteredChapters, setFilteredChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteConfirmChapter, setDeleteConfirmChapter] = useState<Chapter | null>(null);
  const [deletingChapterId, setDeletingChapterId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [lockingChapterId, setLockingChapterId] = useState<number | null>(null);

  // 卷相关状态
  const [volumes, setVolumes] = useState<Volume[]>([]);
  const [expandedVolumes, setExpandedVolumes] = useState<Set<number>>(new Set());
  const [expandedUnassigned, setExpandedUnassigned] = useState(true);
  const [showVolumeDialog, setShowVolumeDialog] = useState(false);
  const [editingVolume, setEditingVolume] = useState<Volume | null>(null);

  // 使用 ref 标记是否正在刷新，防止重复刷新
  const isRefreshingRef = useRef(false);

  // 标记首次加载是否已完成，防止挂载时重复加载
  const hasInitiallyLoadedRef = useRef(false);

  // 获取 token 和 initialized 状态
  const { token, initialized } = useAuthStore();
  const { chaptersRefreshKey, lastUpdatedChapterId } = useRefreshStore();

  // 获取当前分支状态
  const { currentBranch, isMainLine } = useBranchStore();

  // 使用 ref 存储上次的分支ID，用于检测分支切换
  const prevBranchIdRef = useRef<number | null>(null);

  // 动画状态
  const [isTransitioning, setIsTransitioning] = useState(false);

  // 解锁通知状态
  const [showUnlockNotification, setShowUnlockNotification] = useState(false);

  // 使用useRef存储ChapterService实例，确保token变化时重新创建
  const chapterServiceRef = useRef<ChapterService | null>(null);
  const tokenRef = useRef<string | null>(null);

  // 当token或workspaceId变化时重新创建ChapterService
  // 使用全局共享的wsService单例，确保连接状态一致
  if (!chapterServiceRef.current || token !== tokenRef.current) {
    tokenRef.current = token;
    chapterServiceRef.current = externalChapterService || new ChapterService(
      undefined,
      token || undefined,
      workspaceId
    );
  }

  // 使用ref.current进行所有操作
  const chapterService = chapterServiceRef.current;

  const loadChapters = useCallback(async (): Promise<Chapter[]> => {
    // 检查认证状态
    if (!token || !initialized) {
      console.log('[ChapterTree] 认证未完成，跳过加载');
      return [];
    }

    if (!workspaceId) {
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      // 根据当前分支获取章节
      // isMainLine=true 表示主线，传入 null
      // isMainLine=false 表示分支，传入 currentBranch.id
      const branchId = isMainLine ? null : currentBranch?.id;
      const data = await chapterService.listChapters(workspaceId, branchId, true);
      // 按章节序号排序
      const sortedChapters = data.sort((a, b) => a.num - b.num);
      setChapters(sortedChapters);
      setFilteredChapters(sortedChapters);
      return sortedChapters;
    } catch (err) {
      console.error('[ChapterTree] 加载失败:', err);
      setError('加载章节列表失败，请重试');
      return [];
    } finally {
      setLoading(false);
    }
  }, [workspaceId, chapterService, token, initialized, isMainLine, currentBranch]);

  // 加载卷列表
  const loadVolumes = useCallback(async () => {
    if (!workspaceId) return;
    
    try {
      const data = await volumeApi.listVolumes(workspaceId);
      const sortedVolumes = data.sort((a, b) => a.start_chapter - b.start_chapter);
      setVolumes(sortedVolumes);
      
      // 默认展开所有卷
      setExpandedVolumes(new Set(sortedVolumes.map(v => v.id)));
    } catch (err) {
      console.error('[ChapterTree] 加载卷列表失败:', err);
    }
  }, [workspaceId]);

  // 切换卷展开状态
  const toggleVolumeExpand = useCallback((volumeId: number) => {
    setExpandedVolumes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(volumeId)) {
        newSet.delete(volumeId);
      } else {
        newSet.add(volumeId);
      }
      return newSet;
    });
  }, []);

  // 切换未分卷展开状态
  const toggleUnassignedExpand = useCallback(() => {
    setExpandedUnassigned(prev => !prev);
  }, []);

  // 处理卷更新
  const handleVolumeUpdate = useCallback(async (volumeId: number, data: VolumeUpdate) => {
    try {
      await volumeApi.updateVolume(volumeId, data);
      await loadVolumes();
    } catch (error) {
      console.error('更新卷失败:', error);
      throw error;
    }
  }, [loadVolumes]);

  // 处理章节名称更新
  const handleChapterUpdate = useCallback(async (chapterId: number, newName: string) => {
    try {
      const updatedChapter = await chapterService.updateChapter(workspaceId, chapterId, { name: newName });
      // 更新本地状态
      setChapters(prev => prev.map(ch => ch.id === chapterId ? updatedChapter : ch));
      setFilteredChapters(prev => prev.map(ch => ch.id === chapterId ? updatedChapter : ch));
    } catch (error) {
      console.error('更新章节失败:', error);
      throw error;
    }
  }, [workspaceId, chapterService]);

  useEffect(() => {
    // 使用 cancelled 标志防止组件卸载后的状态更新
    let cancelled = false;

    const doLoadChapters = async (): Promise<Chapter[]> => {
      // 检查认证状态
      if (!token || !initialized) {
        console.log('[ChapterTree] 认证未完成，跳过加载');
        return [];
      }

      if (!workspaceId) {
        return [];
      }

      setLoading(true);
      setError(null);

      try {
        // 根据当前分支获取章节
        const branchId = isMainLine ? null : currentBranch?.id;
        const data = await chapterService.listChapters(workspaceId, branchId, true);
        // 按章节序号排序
        const sortedChapters = data.sort((a, b) => a.num - b.num);
        
        // 检查组件是否已卸载
        if (!cancelled) {
          setChapters(sortedChapters);
          setFilteredChapters(sortedChapters);
        }
        return sortedChapters;
      } catch (err) {
        if (!cancelled) {
          const errorMessage = err instanceof Error ? err.message : '加载章节列表失败，请重试';
          console.error('[ChapterTree] 加载失败:', err);
          setError(errorMessage);
        }
        return [];
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    // 首次加载数据
    doLoadChapters();
    loadVolumes();
    hasInitiallyLoadedRef.current = true;

    // 清理函数：设置 cancelled 标志
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 监听分支切换，重新加载章节列表
  useEffect(() => {
    // 首次加载由第一个 useEffect 处理
    if (!hasInitiallyLoadedRef.current) {
      return;
    }

    const currentBranchId: number | null = isMainLine ? null : (currentBranch?.id ?? null);
    
    // 检测分支是否变化
    if (prevBranchIdRef.current !== currentBranchId) {
      prevBranchIdRef.current = currentBranchId;
      
      // 触发过渡动画
      setIsTransitioning(true);
      
      console.log('[ChapterTree] 分支切换，重新加载章节列表', {
        isMainLine,
        branchId: currentBranchId,
      });
      
      // 延迟加载以显示动画效果
      setTimeout(() => {
        loadChapters().finally(() => {
          // 加载完成后结束动画
          setTimeout(() => setIsTransitioning(false), 150);
        });
      }, 150);
    }
  }, [isMainLine, currentBranch, loadChapters]);

  // 监听刷新事件
  useEffect(() => {
    // 首次加载由第一个 useEffect 处理，这里只响应后续刷新
    if (!hasInitiallyLoadedRef.current) {
      return;
    }

    // 移除 chapters.length > 0 条件限制，支持空列表刷新
    // 使用 isRefreshingRef 防止重复刷新请求
    if (!loading && !isRefreshingRef.current) {
      isRefreshingRef.current = true;

      // 如果当前选中的章节被更新，需要在 loadChapters 完成后触发编辑器刷新
      const shouldUpdateCurrentChapter = lastUpdatedChapterId && selectedChapterId === lastUpdatedChapterId;

      loadChapters()
        .then((loadedChapters) => {
          // 在 loadChapters 完成后，使用返回的数据查找更新的章节
          // 传入 false 表示这是数据同步更新，不是用户点击，避免触发折叠逻辑
          if (shouldUpdateCurrentChapter && lastUpdatedChapterId) {
            const updatedChapter = loadedChapters.find(c => c.id === lastUpdatedChapterId);
            if (updatedChapter) {
              onChapterSelect?.(updatedChapter, false);
            }
          }
        })
        .finally(() => {
          isRefreshingRef.current = false;
        });
    }
  }, [chaptersRefreshKey]); // 只依赖刷新键



  const handleChapterSelect = useCallback((chapter: Chapter) => {
    onChapterSelect?.(chapter);
  }, [onChapterSelect]);

  const handleCreateChapter = useCallback(() => {
    setShowCreateDialog(true);
  }, []);

  const handleChapterCreateSuccess = useCallback((newChapter: Chapter) => {
    // 刷新章节列表
    loadChapters();
    // 通知父组件
    onChapterCreate?.();
    // 自动选中新创建的章节
    onChapterSelect?.(newChapter);
  }, [loadChapters, onChapterCreate, onChapterSelect]);

  const handleCreateDialogClose = useCallback(() => {
    setShowCreateDialog(false);
  }, []);

  const handleRefresh = useCallback(() => {
    loadChapters();
    loadVolumes();
  }, [loadChapters, loadVolumes]);

  // 处理搜索输入变化
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.slice(0, 100);
    setSearchQuery(query);
    
    if (query.trim() === '') {
      setFilteredChapters(chapters);
    } else {
      const filtered = chapters.filter(chapter => 
        chapter.name.toLowerCase().includes(query.toLowerCase()) ||
        chapter.num.toString().includes(query)
      );
      setFilteredChapters(filtered);
    }
  }, [chapters]);

  // 获取 triggerChaptersRefresh
  const { triggerChaptersRefresh } = useRefreshStore();

  // 点击删除按钮，显示确认对话框
  const handleDelete = useCallback((chapter: Chapter) => {
    setDeleteConfirmChapter(chapter);
  }, []);

  // 确认删除
  const confirmDelete = useCallback(async () => {
    if (!deleteConfirmChapter) return;

    const isDeletingSelected = selectedChapterId === deleteConfirmChapter.id;

    setDeletingChapterId(deleteConfirmChapter.id);
    try {
      await chapterService.deleteChapter(workspaceId, deleteConfirmChapter.id);
      // 刷新章节列表
      await loadChapters();
      // 触发全局刷新事件
      triggerChaptersRefresh();
      // 如果删除的是当前选中的章节，清空选中状态
      if (isDeletingSelected) {
        // 重新加载后的章节列表，选第一个或者清空
        const branchId = isMainLine ? null : currentBranch?.id;
        const remainingChapters = await chapterService.listChapters(workspaceId, branchId, true);
        if (remainingChapters.length > 0) {
          onChapterSelect?.(remainingChapters[0]);
        }
      }
    } catch (err) {
      console.error('[ChapterTree] 删除章节失败:', err);
      setError('删除章节失败，请重试');
    } finally {
      setDeletingChapterId(null);
      setDeleteConfirmChapter(null);
    }
  }, [deleteConfirmChapter, workspaceId, chapterService, loadChapters, triggerChaptersRefresh, selectedChapterId, onChapterSelect, isMainLine, currentBranch]);

  // 取消删除
  const cancelDelete = useCallback(() => {
    setDeleteConfirmChapter(null);
  }, []);

  // 处理章节锁定切换
  const handleLockToggle = useCallback(async (chapter: Chapter) => {
    if (lockingChapterId === chapter.id || chapter.is_common) {
      return;
    }

    setLockingChapterId(chapter.id);

    try {
      const action = chapter.is_locked ? 'unlock' : 'lock';
      const updatedChapter = await chapterService.lockChapter(workspaceId, chapter.id, action);

      // 更新本地状态
      setChapters(prevChapters =>
        prevChapters.map(ch => ch.id === chapter.id ? updatedChapter : ch)
      );

      // 如果当前选中的章节被更新，触发选中回调
      if (selectedChapterId === chapter.id) {
        onChapterSelect?.(updatedChapter, false);
      }
    } catch (err) {
      console.error('[ChapterTree] 锁定操作失败:', err);
      setError(chapter.is_locked ? '解锁失败，请重试' : '锁定失败，请重试');
    } finally {
      setLockingChapterId(null);
    }
  }, [workspaceId, chapterService, selectedChapterId, onChapterSelect, lockingChapterId]);

  // 处理新增卷
  const handleAddVolume = useCallback(() => {
    setEditingVolume(null);
    setShowVolumeDialog(true);
  }, []);

  // 处理卷保存成功
  const handleVolumeSuccess = useCallback(() => {
    loadVolumes();
  }, [loadVolumes]);

  // 加载状态
  if (loading && chapters.length === 0) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            {ICONS.loadingLarge}
            <p className="mt-2 text-sm text-gray-600">加载中...</p>
          </div>
        </div>

        {/* 创建章节对话框 */}
        <CreateChapterDialog
          workspaceId={workspaceId}
          isOpen={showCreateDialog}
          onClose={handleCreateDialogClose}
          onSuccess={handleChapterCreateSuccess}
          chapterService={chapterService}
          existingChapters={chapters}
        />
      </>
    );
  }

  // 错误状态
  if (error && chapters.length === 0) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-red-600">
            {ICONS.error}
            <p className="text-sm">{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-3 px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150"
            >
              重试
            </button>
          </div>
        </div>

        {/* 创建章节对话框 */}
        <CreateChapterDialog
          workspaceId={workspaceId}
          isOpen={showCreateDialog}
          onClose={handleCreateDialogClose}
          onSuccess={handleChapterCreateSuccess}
          chapterService={chapterService}
          existingChapters={chapters}
        />
      </>
    );
  }

  // 空状态
  if (chapters.length === 0 && !loading) {
    return (
      <>
        <div className="h-full flex flex-col">
          {/* 顶部搜索栏 */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  {ICONS.search}
                </div>
                <input
                  type="text"
                  placeholder="搜索章节..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white transition-all"
                />
              </div>
              <button
                onClick={handleRefresh}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="刷新"
              >
                {ICONS.refresh}
              </button>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              {ICONS.empty}
              <p className="text-sm text-gray-600 mb-3">暂无章节</p>
              {onChapterCreate && (
                <AddMenu
                  onAddChapter={handleCreateChapter}
                  onAddVolume={handleAddVolume}
                />
              )}
            </div>
          </div>
        </div>

        {/* 创建章节对话框 */}
        <CreateChapterDialog
          workspaceId={workspaceId}
          isOpen={showCreateDialog}
          onClose={handleCreateDialogClose}
          onSuccess={handleChapterCreateSuccess}
          chapterService={chapterService}
          existingChapters={chapters}
        />

        {/* 卷表单对话框 */}
        <VolumeFormDialog
          isOpen={showVolumeDialog}
          onClose={() => setShowVolumeDialog(false)}
          workspaceId={workspaceId}
          volumes={volumes}
          onSuccess={handleVolumeSuccess}
          editingVolume={editingVolume}
        />
      </>
    );
  }

  // 正常状态
  return (
    <>
      <div className="h-full flex flex-col">
        {/* 顶部搜索栏 */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                {ICONS.search}
              </div>
              <input
                type="text"
                placeholder="搜索章节..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white transition-all"
              />
            </div>
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="刷新"
            >
              {ICONS.refresh}
            </button>
          </div>
          {/* 分支指示器 */}
          {!isMainLine && currentBranch && (
            <div className="mt-2">
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full transition-all duration-150">
                {currentBranch.name}
              </span>
            </div>
          )}
        </div>

        {/* 新增按钮 */}
        {onChapterCreate && (
          <div className="p-4 border-b border-gray-200">
            <AddMenu
              onAddChapter={handleCreateChapter}
              onAddVolume={handleAddVolume}
            />
          </div>
        )}

        {/* 章节列表 - 按卷分组 */}
        <div className={`flex-1 overflow-y-auto p-4 space-y-2 transition-all duration-150 ease-in-out ${isTransitioning ? 'opacity-0 translate-x-2' : 'opacity-100 translate-x-0'}`}>
          {searchQuery ? (
            // 搜索模式：平铺显示搜索结果
            filteredChapters.map((chapter) => (
              <ChapterItem
                key={chapter.id}
                chapter={chapter}
                isSelected={selectedChapterId === chapter.id}
                onSelect={handleChapterSelect}
                onDelete={handleDelete}
                onLockToggle={handleLockToggle}
                isLocking={lockingChapterId === chapter.id}
                onChapterUpdate={handleChapterUpdate}
              />
            ))
          ) : (
            // 正常模式：按卷分组显示
            <>
              {volumes.map((volume) => (
                <VolumeGroup
                  key={volume.id}
                  volume={volume}
                  chapters={filteredChapters}
                  selectedChapterId={selectedChapterId}
                  expanded={expandedVolumes.has(volume.id)}
                  onToggle={() => toggleVolumeExpand(volume.id)}
                  onSelect={handleChapterSelect}
                  onDelete={handleDelete}
                  onLockToggle={handleLockToggle}
                  lockingChapterId={lockingChapterId}
                  onVolumeUpdate={handleVolumeUpdate}
                />
              ))}
              <UnassignedGroup
                chapters={filteredChapters}
                volumes={volumes}
                selectedChapterId={selectedChapterId}
                expanded={expandedUnassigned}
                onToggle={toggleUnassignedExpand}
                onSelect={handleChapterSelect}
                onDelete={handleDelete}
                onLockToggle={handleLockToggle}
                lockingChapterId={lockingChapterId}
              />
            </>
          )}
          {filteredChapters.length === 0 && searchQuery && (
            <div className="text-center py-8 text-gray-500 text-sm">
              没有找到匹配的章节
            </div>
          )}
        </div>

        {/* 加载指示器 */}
        {loading && chapters.length > 0 && (
          <div className="p-4 text-center">
            {ICONS.loadingSmall}
          </div>
        )}

        {/* 底部统计信息 */}
        {!loading && (
          <div className="p-4 border-t border-gray-200 text-center text-xs text-gray-500">
            共 {filteredChapters.length} 个章节
          </div>
        )}
      </div>

      {/* 创建章节对话框 */}
      <CreateChapterDialog
        workspaceId={workspaceId}
        isOpen={showCreateDialog}
        onClose={handleCreateDialogClose}
        onSuccess={handleChapterCreateSuccess}
        chapterService={chapterService}
        existingChapters={chapters}
      />

      {/* 卷表单对话框 */}
      <VolumeFormDialog
        isOpen={showVolumeDialog}
        onClose={() => setShowVolumeDialog(false)}
        workspaceId={workspaceId}
        volumes={volumes}
        onSuccess={handleVolumeSuccess}
        editingVolume={editingVolume}
      />

      {/* 删除确认对话框 */}
      {deleteConfirmChapter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-sm">
            <h3 className="text-lg font-medium text-gray-900 mb-2">确认删除章节</h3>
            <p className="text-sm text-gray-600 mb-4">
              确定要删除「第{deleteConfirmChapter.num}章 {deleteConfirmChapter.name}」吗？
              <br />
              此操作不可撤销，章节内容将被永久删除。
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                disabled={deletingChapterId === deleteConfirmChapter.id}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletingChapterId === deleteConfirmChapter.id}
                className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {deletingChapterId === deleteConfirmChapter.id ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    删除中...
                  </>
                ) : (
                  <>
                    {ICONS.delete}
                    确认删除
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 解锁通知 Toast */}
      {showUnlockNotification && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-in-right">
          <div className="bg-emerald-500 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-medium">所有分支已删除</p>
              <p className="text-sm opacity-90">公共章节已解锁，现在可以编辑了</p>
            </div>
            <button
              onClick={() => setShowUnlockNotification(false)}
              className="ml-2 text-white opacity-70 hover:opacity-100 transition-opacity"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChapterTree;
